// ════════════════════════════════════════════════════════════════════════
//  Stars for one lesson, from the stored progress of its stages.
//
//  Uses the same function as the learn app so child and teacher see the same
//  number.
//
//  Computes with the REAL accuracy. Deriving it from the stars (`stars / 3`)
//  looks harmless and is wrong: three stages with two stars each would come to
//  0.67 and show one star for the lesson instead of two. That bug was in here
//  once, which is why this lives in its own file with its own test.
// ════════════════════════════════════════════════════════════════════════

import { lessonStars, type Lesson, type StageResult } from "@otto/lehrgang";
import type { StudentRow } from "./data.js";

export type LessonMark = {
  /** True when every stage of the lesson has been practised. */
  readonly complete: boolean;
  readonly stars: 1 | 2 | 3 | null;
};

export function starsForLesson(
  lesson: Lesson,
  row: StudentRow,
  thresholds: Parameters<typeof lessonStars>[1],
): LessonMark | null {
  const results: StageResult[] = lesson.stages
    .map((stage) => ({ stage, mark: row.stages[stage.key] }))
    .filter(
      (x): x is { stage: Lesson["stages"][number]; mark: NonNullable<typeof x.mark> } =>
        x.mark !== undefined,
    )
    .map(({ stage, mark }) => ({
      stageKey: stage.key,
      accuracy: mark.accuracy,
      stars: mark.stars,
    }));

  if (results.length === 0) return null;
  return {
    complete: results.length === lesson.stages.length,
    stars: lessonStars(results, thresholds),
  };
}
